import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PlatformUser } from '@nucleus-commerce/api-client/platform';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';
import { Button } from '@nucleus-commerce/ui/components/button';

import { ServerError } from '@/shared/ui/server-error';

import { useUpdateUser } from '../hooks/use-update-user';
import { editErrorKey } from '../schemas/user-rules';

export type UserStatusAction = 'disable' | 'enable';

export interface UserStatusDialogProps {
  /** The transition being confirmed; null closes the dialog. */
  pending: { action: UserStatusAction; user: PlatformUser } | null;
  onClose: () => void;
}

/** Confirmation for disable/enable. Disabling ends the person's sessions, so it asks first. */
export function UserStatusDialog({ pending, onClose }: UserStatusDialogProps) {
  // Keep the last request while the close animation plays so the content does not vanish.
  const [last, setLast] = useState(pending);
  if (pending !== null && pending !== last) setLast(pending);
  const current = pending ?? last;

  return (
    <AlertDialog
      open={pending !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <AlertDialogContent>
        {current ? (
          <StatusForm
            key={`${current.action}:${current.user.id}`}
            action={current.action}
            user={current.user}
            onDone={onClose}
          />
        ) : null}
      </AlertDialogContent>
    </AlertDialog>
  );
}

interface StatusFormProps {
  action: UserStatusAction;
  user: PlatformUser;
  onDone: () => void;
}

function StatusForm({ action, user, onDone }: StatusFormProps) {
  const { t } = useTranslation(['users', 'common']);
  const update = useUpdateUser(`users:toasts.${action === 'disable' ? 'disabled' : 'enabled'}`);
  const errorKey = update.isError ? editErrorKey(update.error) : null;

  const confirm = () =>
    update.mutate(
      { userId: user.id, body: { status: action === 'disable' ? 'disabled' : 'active' } },
      { onSuccess: onDone },
    );

  return (
    <div className="flex flex-col gap-4">
      <AlertDialogHeader>
        <AlertDialogTitle>{t(`users:statusDialog.${action}.title`)}</AlertDialogTitle>
        <AlertDialogDescription>
          {t(`users:statusDialog.${action}.description`, { name: user.name })}
        </AlertDialogDescription>
      </AlertDialogHeader>
      {update.isError ? (
        <ServerError error={update.error} message={errorKey ? t(errorKey) : null} />
      ) : null}
      <AlertDialogFooter>
        <AlertDialogCancel disabled={update.isPending}>
          {t('common:actions.cancel')}
        </AlertDialogCancel>
        <Button
          type="button"
          variant={action === 'disable' ? 'destructive' : 'default'}
          disabled={update.isPending}
          onClick={confirm}
        >
          {t(`users:statusDialog.${action}.confirm`)}
        </Button>
      </AlertDialogFooter>
    </div>
  );
}
